import React, { useState, useEffect } from 'react';

const formatWithCommas = (val) => {
  if (!val && val !== 0) return '';
  const num = String(val).replace(/,/g, '');
  if (num === '' || num === '-') return num;
  const pts = num.split('.');
  pts[0] = pts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return pts.join('.');
};

const LuxuryInput = ({ label, value, onChange, prefix, suffix, labelClass, inputClass, affixClass, disabled }) => {
  const [display, setDisplay] = useState(formatWithCommas(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDisplay(formatWithCommas(value));
    }
  }, [value, focused]);

  const handleChange = (e) => {
    if (disabled) return;
    const raw = e.target.value.replace(/,/g, '');
    if (raw === '' || raw === '-' || /^-?\d*\.?\d*$/.test(raw)) {
      setDisplay(e.target.value);
      onChange(raw);
    }
  };

  return (
    <div className="group">
      <label className={`${labelClass} ${disabled ? 'opacity-50' : ''}`}>{label}</label>
      <div className="relative">
        {prefix && <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${affixClass} ${disabled ? 'opacity-50' : ''}`}>{prefix}</span>}
        <input
          type="text"
          inputMode="decimal"
          value={display}
          onChange={handleChange}
          onFocus={(e) => { if (!disabled) { setFocused(true); e.target.select(); } }}
          onBlur={() => { setFocused(false); setDisplay(formatWithCommas(value)); }}
          disabled={disabled}
          className={`${inputClass} ${prefix ? 'pl-8' : 'pl-4'} ${suffix ? 'pr-12' : 'pr-4'} ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-200/30' : ''}`}
        />
        {suffix && <span className={`absolute right-4 top-1/2 -translate-y-1/2 text-sm ${affixClass} ${disabled ? 'opacity-50' : ''}`}>{suffix}</span>}
      </div>
    </div>
  );
};

const themes = {
  violetDream: { name: 'Violet Dream', bg: 'from-violet-50 via-purple-50 to-fuchsia-50', glow1: 'bg-violet-200/30', glow2: 'bg-purple-200/30', sidebar: 'border-violet-300/50 bg-white/70', accentBg: 'bg-violet-100/50', accentBorder: 'border-violet-400/50', accentGradient: 'from-violet-200/60 to-violet-100/60', buttonGradient: 'from-violet-600 to-violet-500', inputBorder: 'border-violet-300/60', inputFocus: 'focus:border-violet-500/50', labelText: 'text-violet-700', highlight: 'text-violet-600', card1: 'from-white via-violet-50/50 to-white border-violet-300/50', card2: 'from-purple-50 via-white to-white border-purple-300/50', card3: 'from-fuchsia-50 via-white to-white border-fuchsia-300/50', banner: 'from-violet-100/50 via-white to-violet-100/50 border-violet-400/50', bannerText: 'text-violet-700', isLight: true },
  slateModern: { name: 'Slate Modern', bg: 'from-slate-100 via-gray-50 to-slate-100', glow1: 'bg-slate-300/20', glow2: 'bg-gray-300/20', sidebar: 'border-slate-300/50 bg-white/70', accentBg: 'bg-slate-200/50', accentBorder: 'border-slate-400/50', accentGradient: 'from-slate-200/60 to-slate-100/60', buttonGradient: 'from-slate-700 to-slate-600', inputBorder: 'border-slate-300/60', inputFocus: 'focus:border-slate-500/50', labelText: 'text-slate-600', highlight: 'text-slate-800', card1: 'from-white via-slate-50/50 to-white border-slate-300/50', card2: 'from-gray-50 via-white to-white border-gray-300/50', card3: 'from-zinc-50 via-white to-white border-zinc-300/50', banner: 'from-slate-200/50 via-white to-slate-200/50 border-slate-400/50', bannerText: 'text-slate-800', isLight: true },
  skyBlue: { name: 'Sky Blue', bg: 'from-sky-50 via-blue-50 to-indigo-50', glow1: 'bg-sky-200/30', glow2: 'bg-blue-200/30', sidebar: 'border-sky-300/50 bg-white/70', accentBg: 'bg-sky-100/50', accentBorder: 'border-sky-400/50', accentGradient: 'from-sky-200/60 to-sky-100/60', buttonGradient: 'from-sky-600 to-sky-500', inputBorder: 'border-sky-300/60', inputFocus: 'focus:border-sky-500/50', labelText: 'text-sky-700', highlight: 'text-sky-600', card1: 'from-white via-sky-50/50 to-white border-sky-300/50', card2: 'from-blue-50 via-white to-white border-blue-300/50', card3: 'from-indigo-50 via-white to-white border-indigo-300/50', banner: 'from-sky-100/50 via-white to-sky-100/50 border-sky-400/50', bannerText: 'text-sky-700', isLight: true },
  babyBlue: { name: 'Baby Blue', bg: 'from-blue-100 via-sky-50 to-blue-100', glow1: 'bg-blue-200/30', glow2: 'bg-sky-200/30', sidebar: 'border-blue-200/50 bg-white/70', accentBg: 'bg-blue-100/50', accentBorder: 'border-blue-300/50', accentGradient: 'from-blue-200/60 to-blue-100/60', buttonGradient: 'from-blue-500 to-blue-400', inputBorder: 'border-blue-200/60', inputFocus: 'focus:border-blue-400/50', labelText: 'text-blue-600', highlight: 'text-blue-600', card1: 'from-white via-blue-50/50 to-white border-blue-200/50', card2: 'from-sky-50 via-white to-white border-sky-200/50', card3: 'from-blue-50 via-white to-white border-blue-200/50', banner: 'from-blue-100/50 via-white to-blue-100/50 border-blue-300/50', bannerText: 'text-blue-700', isLight: true },
};

const LuxuryCalculator = () => {
  const [currentTheme, setCurrentTheme] = useState('violetDream');
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('current-asset');
  const [taxMethod, setTaxMethod] = useState('net');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [cashValuePaste, setCashValuePaste] = useState('');
  const [deathBenefitPaste, setDeathBenefitPaste] = useState('');
  const [yearlyData, setYearlyData] = useState([]);
  const [baseYearlyData, setBaseYearlyData] = useState([]);
  const [basePremium, setBasePremium] = useState(0);
  const [popupPremium, setPopupPremium] = useState('0');
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(1);
  const [includeManagementFees, setIncludeManagementFees] = useState(false);
  const [includeExternalCosts, setIncludeExternalCosts] = useState(false);
  const [includeRecoveredCosts, setIncludeRecoveredCosts] = useState(false);
  
  const theme = themes[currentTheme] || themes.midnightSapphire;
  const isLight = theme.isLight;

  const [currentAsset, setCurrentAsset] = useState({
    capitalInput: '0', existingCapital: '0', timePeriod: '0', taxes: '0',
    compoundingRate: '0', termCosts: '0', opportunityCost: '0', managementFees: '0', planningFees: '0'
  });

  // Reset selectedYear if it exceeds time period
  useEffect(() => {
    const maxYear = parseInt(currentAsset.timePeriod) || 1;
    if (selectedYear > maxYear) {
      setSelectedYear(Math.max(1, maxYear));
    }
  }, [currentAsset.timePeriod, selectedYear]);

  const [manualPliInputs, setManualPliInputs] = useState({
    annualPremium: '0', futureCashValue: '0', futureDeathBenefit: '0'
  });

  const handleInputChange = (field, value) => setCurrentAsset(prev => ({ ...prev, [field]: value }));
  const handleManualPliChange = (field, value) => setManualPliInputs(prev => ({ ...prev, [field]: value }));

  const parseColumnData = (text) => {
    if (!text.trim()) return [];
    return text.trim().split('\n').map(line => {
      const cleaned = line.trim().replace(/[$,]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    });
  };

  const applyPastedData = () => {
    const cvValues = parseColumnData(cashValuePaste);
    const dbValues = parseColumnData(deathBenefitPaste);
    const maxLength = Math.max(cvValues.length, dbValues.length);
    const premium = parseFloat(popupPremium) || 0;
    
    if (maxLength === 0) return;
    
    const data = [];
    for (let i = 0; i < maxLength; i++) {
      data.push({
        year: i + 1,
        premium: premium,
        cashValue: cvValues[i] || 0,
        deathBenefit: dbValues[i] || 0
      });
    }
    
    setBaseYearlyData(data);
    setYearlyData(data);
    setBasePremium(premium);
    
    // Sync to main annual premium
    setManualPliInputs(prev => ({
      ...prev,
      annualPremium: String(premium),
      futureCashValue: String(data[data.length - 1].cashValue),
      futureDeathBenefit: String(data[data.length - 1].deathBenefit)
    }));
    
    setIsDetailsOpen(false);
  };

  const clearYearlyData = () => {
    setYearlyData([]);
    setBaseYearlyData([]);
    setBasePremium(0);
    setCashValuePaste('');
    setDeathBenefitPaste('');
    setPopupPremium('0');
  };

  // Proportional scaling when main annual premium changes
  useEffect(() => {
    if (basePremium > 0 && baseYearlyData.length > 0) {
      const newPremium = parseFloat(manualPliInputs.annualPremium) || 0;
      const scaleFactor = newPremium / basePremium;
      
      const scaledData = baseYearlyData.map(row => ({
        year: row.year,
        premium: newPremium,
        cashValue: row.cashValue * scaleFactor,
        deathBenefit: row.deathBenefit * scaleFactor
      }));
      
      setYearlyData(scaledData);
      
      // Update final values
      if (scaledData.length > 0) {
        const lastYear = scaledData[scaledData.length - 1];
        setManualPliInputs(prev => ({
          ...prev,
          futureCashValue: String(Math.round(lastYear.cashValue)),
          futureDeathBenefit: String(Math.round(lastYear.deathBenefit))
        }));
      }
    }
  }, [manualPliInputs.annualPremium, basePremium, baseYearlyData]);

  const getYearData = (year) => {
    if (yearlyData.length === 0) return null;
    if (year <= yearlyData.length) {
      return yearlyData[year - 1];
    }
    return yearlyData[yearlyData.length - 1];
  };

  const calculateIRR = (cashFlows) => {
    // Use bisection method for reliability
    const npv = (rate) => {
      let total = 0;
      for (let i = 0; i < cashFlows.length; i++) {
        total += cashFlows[i] / Math.pow(1 + rate, i);
      }
      return total;
    };
    
    let low = -0.99;
    let high = 1.0;
    
    // Check if solution exists in range
    const npvLow = npv(low);
    const npvHigh = npv(high);
    
    // If both same sign, try to find bounds
    if (npvLow * npvHigh > 0) {
      // Try wider range for high
      for (let testHigh of [2, 5, 10]) {
        if (npv(low) * npv(testHigh) < 0) {
          high = testHigh;
          break;
        }
      }
    }
    
    // Bisection method - very reliable
    for (let i = 0; i < 100; i++) {
      const mid = (low + high) / 2;
      const npvMid = npv(mid);
      
      if (Math.abs(npvMid) < 0.01 || Math.abs(high - low) < 0.0001) {
        return mid;
      }
      
      if (npv(low) * npvMid < 0) {
        high = mid;
      } else {
        low = mid;
      }
    }
    
    return (low + high) / 2;
  };

  const getManualPliAnalysis = () => {
    const years = parseInt(currentAsset.timePeriod) || 0;
    const premium = parseFloat(manualPliInputs.annualPremium) || 0;
    
    if (years === 0 || premium === 0) return { cashValueIRR: 0, deathBenefitIRR: 0, totalPremiums: 0, years: 0, premium: 0, cashValue: 0, deathBenefit: 0 };
    
    // Get values for the specific time horizon year
    let cashValue, deathBenefit;
    const yearData = getYearData(years);
    
    if (yearData) {
      // Use yearly data for the time horizon year
      cashValue = yearData.cashValue;
      deathBenefit = yearData.deathBenefit;
    } else {
      // Fall back to manual inputs
      cashValue = parseFloat(manualPliInputs.futureCashValue) || 0;
      deathBenefit = parseFloat(manualPliInputs.futureDeathBenefit) || 0;
    }
    
    const totalPremiums = premium * years;
    
    // Cash Value IRR: pay premium each year, receive cash value at end
    let cashValueIRR = 0;
    if (cashValue > 0) {
      const cashFlows = [];
      for (let y = 0; y < years; y++) {
        cashFlows.push(-premium); // pay premium each year
      }
      cashFlows[years - 1] += cashValue; // receive cash value at end of final year
      cashValueIRR = calculateIRR(cashFlows) * 100;
    }
    
    // Death Benefit IRR: same structure but with death benefit
    let deathBenefitIRR = 0;
    if (deathBenefit > 0) {
      const cashFlows = [];
      for (let y = 0; y < years; y++) {
        cashFlows.push(-premium);
      }
      cashFlows[years - 1] += deathBenefit;
      deathBenefitIRR = calculateIRR(cashFlows) * 100;
    }
    
    return { cashValueIRR, deathBenefitIRR, totalPremiums, years, premium, cashValue, deathBenefit };
  };

  const calculateNetTaxOnlyValue = () => {
    const annual = parseFloat(currentAsset.capitalInput) || 0;
    const current = parseFloat(currentAsset.existingCapital) || 0;
    const rate = (parseFloat(currentAsset.compoundingRate) || 0) / 100;
    const time = parseFloat(currentAsset.timePeriod) || 0;
    const taxes = (parseFloat(currentAsset.taxes) || 0) / 100;
    if (time === 0) return 0;
    let val = current;
    if (taxMethod === 'net') {
      for (let y = 1; y <= time; y++) { val += annual; const g = val * rate; val += g - (g * taxes); }
    } else {
      val = current * Math.pow(1 + rate, time);
      if (annual > 0 && rate > 0) val += annual * ((Math.pow(1 + rate, time + 1) - (1 + rate)) / rate);
      else if (annual > 0) val += annual * time;
    }
    return Math.max(0, val);
  };

  const calculateProjectedValue = () => {
    const annual = parseFloat(currentAsset.capitalInput) || 0;
    const current = parseFloat(currentAsset.existingCapital) || 0;
    const rate = (parseFloat(currentAsset.compoundingRate) || 0) / 100;
    const time = parseFloat(currentAsset.timePeriod) || 0;
    const taxes = (parseFloat(currentAsset.taxes) || 0) / 100;
    const mgmt = (parseFloat(currentAsset.managementFees) || 0) / 100;
    if (time === 0) return 0;
    let val = current;
    if (taxMethod === 'net') {
      for (let y = 1; y <= time; y++) { val += annual; const b = val; const g = b * rate; val += g - (g * taxes) - (b * mgmt); }
    } else {
      const nr = rate - mgmt;
      val = current * Math.pow(1 + nr, time);
      if (annual > 0 && nr > 0) val += annual * ((Math.pow(1 + nr, time + 1) - (1 + nr)) / nr);
      else if (annual > 0) val += annual * time;
    }
    return Math.max(0, val);
  };

  const getCalculationSummary = () => {
    const annual = parseFloat(currentAsset.capitalInput) || 0;
    const current = parseFloat(currentAsset.existingCapital) || 0;
    const rate = (parseFloat(currentAsset.compoundingRate) || 0) / 100;
    const time = parseFloat(currentAsset.timePeriod) || 0;
    const taxes = (parseFloat(currentAsset.taxes) || 0) / 100;
    const opp = (parseFloat(currentAsset.opportunityCost) || 0) / 100;
    const mgmt = (parseFloat(currentAsset.managementFees) || 0) / 100;
    const term = parseFloat(currentAsset.termCosts) || 0;
    const plan = parseFloat(currentAsset.planningFees) || 0;
    if (time === 0) return { currentAssets: 0, totalPrincipal: 0, accountValue: 0, accountGains: 0, taxAmount: 0, managementFeesAmount: 0, totalTermCosts: 0, termCostOpportunity: 0, totalPlanningFees: 0, planningFeeOpportunity: 0, taxOpportunityCost: 0 };
    const totalPrincipal = current + (annual * time);
    let val = current, totalTax = 0, totalMgmt = 0;
    if (taxMethod === 'net') {
      for (let y = 1; y <= time; y++) { val += annual; const b = val; const g = b * rate; totalTax += g * taxes; totalMgmt += b * mgmt; val += g - (g * taxes) - (b * mgmt); }
    } else {
      let run = current;
      for (let y = 1; y <= time; y++) { run += annual; totalMgmt += run * mgmt; run *= (1 + rate - mgmt); }
      const nr = rate - mgmt;
      val = current * Math.pow(1 + nr, time);
      if (annual > 0 && nr > 0) val += annual * ((Math.pow(1 + nr, time + 1) - (1 + nr)) / nr);
      else if (annual > 0) val += annual * time;
      totalTax = (val - totalPrincipal) * taxes;
    }
    const totalTermCosts = term * time;
    const termOpp = term > 0 && opp > 0 ? (term * ((Math.pow(1 + opp, time) - 1) / opp)) - totalTermCosts : 0;
    const totalPlanningFees = plan * time;
    const planOpp = plan > 0 && opp > 0 ? (plan * ((Math.pow(1 + opp, time) - 1) / opp)) - totalPlanningFees : 0;
    const taxOpp = taxMethod === 'gross' && totalTax > 0 && opp > 0 ? totalTax * opp * (time / 2) : 0;
    return { currentAssets: current, totalPrincipal, accountValue: val, accountGains: val - totalPrincipal, taxAmount: totalTax, managementFeesAmount: totalMgmt, totalTermCosts, termCostOpportunity: termOpp, totalPlanningFees, planningFeeOpportunity: planOpp, taxOpportunityCost: taxOpp };
  };

  const getComparison = () => {
    const s = getCalculationSummary();
    const maxTime = parseFloat(currentAsset.timePeriod) || 1;
    const time = Math.min(selectedYear, maxTime);
    const annual = parseFloat(currentAsset.capitalInput) || 0;
    const current = parseFloat(currentAsset.existingCapital) || 0;
    const rate = (parseFloat(currentAsset.compoundingRate) || 0) / 100;
    const taxes = (parseFloat(currentAsset.taxes) || 0) / 100;
    const opp = (parseFloat(currentAsset.opportunityCost) || 0) / 100;
    const mgmt = (parseFloat(currentAsset.managementFees) || 0) / 100;
    const term = parseFloat(currentAsset.termCosts) || 0;
    const plan = parseFloat(currentAsset.planningFees) || 0;
    
    // Calculate account value at selected year
    let accVal = current;
    if (taxMethod === 'net') {
      for (let y = 1; y <= time; y++) { accVal += annual; const g = accVal * rate; accVal += g - (g * taxes); }
    } else {
      accVal = current * Math.pow(1 + rate, time);
      if (annual > 0 && rate > 0) accVal += annual * ((Math.pow(1 + rate, time + 1) - (1 + rate)) / rate);
      else if (annual > 0) accVal += annual * time;
    }
    
    // Calculate projected value with fees at selected year
    let projVal = current;
    if (taxMethod === 'net') {
      for (let y = 1; y <= time; y++) { projVal += annual; const b = projVal; const g = b * rate; projVal += g - (g * taxes) - (b * mgmt); }
    } else {
      const nr = rate - mgmt;
      projVal = current * Math.pow(1 + nr, time);
      if (annual > 0 && nr > 0) projVal += annual * ((Math.pow(1 + nr, time + 1) - (1 + nr)) / nr);
      else if (annual > 0) projVal += annual * time;
    }
    
    // Calculate external costs at selected year
    const totalTermCosts = term * time;
    const termOpp = term > 0 && opp > 0 ? (term * ((Math.pow(1 + opp, time) - 1) / opp)) - totalTermCosts : 0;
    const totalPlanningFees = plan * time;
    const planOpp = plan > 0 && opp > 0 ? (plan * ((Math.pow(1 + opp, time) - 1) / opp)) - totalPlanningFees : 0;
    const totalPrincipal = current + (annual * time);
    let taxAmount = 0;
    if (taxMethod === 'gross') {
      taxAmount = (accVal - totalPrincipal) * taxes;
    }
    const taxOpp = taxMethod === 'gross' && taxAmount > 0 && opp > 0 ? taxAmount * opp * (time / 2) : 0;
    
    const extCosts = includeExternalCosts ? (taxMethod === 'gross' ? taxAmount + taxOpp : 0) + totalTermCosts + termOpp + totalPlanningFees + planOpp : 0;
    const netVal = (includeManagementFees ? projVal : accVal) - extCosts;
    
    let extIRR = 0;
    if (time > 0) { const cf = [current > 0 ? -(current + annual) : -annual]; for (let i = 1; i < time; i++) cf.push(-annual); cf.push(netVal); extIRR = calculateIRR(cf) * 100; }
    const external = { accountValue: accVal, totalPrincipal, accountGains: accVal - totalPrincipal, netValue: netVal, externalRateOfReturn: extIRR };
    
    // PLI calculations at selected year
    const pli = getManualPliAnalysis();
    let internal = null;
    if (pli.years > 0 && pli.premium > 0 && time <= pli.years) {
      let cvAtYear, dbAtYear;
      
      // Check if we have yearly data
      const yearData = getYearData(time);
      if (yearData) {
        cvAtYear = yearData.cashValue;
        dbAtYear = yearData.deathBenefit;
      } else {
        // Interpolate cash value and death benefit based on selected year
        const yearRatio = time / pli.years;
        cvAtYear = pli.cashValue * yearRatio;
        dbAtYear = pli.premium * time + (pli.deathBenefit - pli.premium * pli.years) * yearRatio;
      }
      
      const rec = includeRecoveredCosts ? (taxMethod === 'gross' ? taxAmount + taxOpp : 0) + totalTermCosts + termOpp + totalPlanningFees + planOpp : 0;
      const netDB = dbAtYear - cvAtYear;
      const total = cvAtYear + netDB + rec;
      
      let pliIRR = 0;
      if (time > 0) { const cf = []; for (let i = 0; i < time; i++) cf.push(-pli.premium); cf[cf.length - 1] += total; pliIRR = calculateIRR(cf) * 100; }
      
      // Cash Value IRR at selected year
      let cvIRR = 0;
      if (cvAtYear > 0 && time > 0) {
        const cvCF = [];
        for (let i = 0; i < time; i++) cvCF.push(-pli.premium);
        cvCF[time - 1] += cvAtYear;
        cvIRR = calculateIRR(cvCF) * 100;
      }
      
      internal = { finalCashValue: cvAtYear, finalDeathBenefit: dbAtYear, netDeathBenefit: netDB, totalPremiums: pli.premium * time, recoveredCosts: rec, totalAssets: total, cashValueIRR: cvIRR, externalRateOfReturn: pliIRR };
    }
    return { external, internal };
  };

  const tabs = [{ id: 'current-asset', label: 'Current Asset', icon: '◈' }, { id: 'pli-values', label: 'Life Insurance', icon: '◆' }, { id: 'comparison', label: 'Comparison', icon: '◇' }];
  const inputFields = [
    { key: 'timePeriod', label: 'Time Horizon', suffix: 'years' }, { key: 'capitalInput', label: 'Annual Contribution', prefix: '$' },
    { key: 'existingCapital', label: 'Current Assets', prefix: '$' }, { key: 'compoundingRate', label: 'Growth Rate', suffix: '%' },
    { key: 'taxes', label: 'Tax Rate', suffix: '%' }, { key: 'managementFees', label: 'Management Fee', suffix: '%' },
    { key: 'termCosts', label: 'Term Insurance', prefix: '$' }, { key: 'planningFees', label: 'Planning Fees', prefix: '$' },
    { key: 'opportunityCost', label: 'Opportunity Cost', suffix: '%' }
  ];

  const summary = getCalculationSummary();
  const comparison = getComparison();
  const pliAnalysis = getManualPliAnalysis();

  const formatMoney = (val) => `${Math.round(val).toLocaleString()}`;

  const textColor = isLight ? 'text-stone-800' : 'text-white';
  const textMuted = isLight ? 'text-stone-600' : 'text-stone-400';
  const textSubtle = isLight ? 'text-stone-500' : 'text-stone-500';
  const cardBg = isLight ? 'bg-white/60' : 'bg-stone-900/50';
  const cardBorder = isLight ? 'border-stone-200/50' : 'border-stone-800/50';
  const labelClass = `block text-xs font-medium ${theme.labelText} uppercase tracking-widest mb-2`;
  const inputClass = `w-full ${isLight ? 'bg-white/60' : 'bg-black/30'} border ${theme.inputBorder} rounded-xl py-2.5 ${textColor} text-right font-light tracking-wide focus:outline-none ${theme.inputFocus} transition-all duration-300`;
  const affixClass = isLight ? 'text-stone-400' : 'text-stone-500';

  const Toggle = ({ checked, onChange, label }) => (
    <label className="flex items-center justify-between py-3 cursor-pointer group">
      <span className={`text-sm ${textMuted}`}>{label}</span>
      <div className={`w-12 h-6 rounded-full p-1 transition-all duration-300 ${checked ? `bg-gradient-to-r ${theme.accentGradient}` : isLight ? 'bg-stone-300' : 'bg-stone-700'}`} onClick={onChange}>
        <div className={`w-4 h-4 rounded-full bg-white shadow-lg transition-transform duration-300 ${checked ? 'translate-x-6' : 'translate-x-0'}`} />
      </div>
    </label>
  );

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bg} ${textColor} font-light`}>
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-1/4 w-96 h-96 ${theme.glow1} rounded-full blur-3xl`} />
        <div className={`absolute bottom-0 right-1/4 w-96 h-96 ${theme.glow2} rounded-full blur-3xl`} />
      </div>

      <div className="relative flex min-h-screen">
        <div className={`${isSidebarOpen ? 'w-72' : 'w-0'} transition-all duration-500 ease-out overflow-hidden border-r ${theme.sidebar} backdrop-blur-sm`}>
          <div className="w-72 p-6">
            <div className="mb-8">
              <h2 className={`text-xs font-medium ${theme.labelText} uppercase tracking-widest mb-1`}>Parameters</h2>
              <div className="w-12 h-px bg-gradient-to-r from-current to-transparent opacity-50" />
            </div>
            <div className="space-y-5">
              {inputFields.map((f) => (
                <React.Fragment key={f.key}>
                  <LuxuryInput label={f.label} value={currentAsset[f.key]} onChange={(v) => handleInputChange(f.key, v)} prefix={f.prefix} suffix={f.suffix} labelClass={labelClass} inputClass={inputClass} affixClass={affixClass} disabled={f.key === 'existingCapital'} />
                  {f.key === 'taxes' && (
                    <div className="flex gap-2 mt-2">
                      {[{ val: 'net', label: 'Net Tax' }, { val: 'gross', label: 'Gross Tax' }].map((opt) => (
                        <div key={opt.val} onClick={() => setTaxMethod(opt.val)} className={`flex-1 flex items-center justify-center p-2 rounded-lg cursor-pointer transition-all duration-300 ${taxMethod === opt.val ? `${theme.accentBg} border ${theme.accentBorder}` : `border border-transparent ${isLight ? 'hover:bg-stone-100/50' : 'hover:bg-white/5'}`}`}>
                          <div className={`w-3 h-3 rounded-full border-2 mr-2 flex items-center justify-center transition-all ${taxMethod === opt.val ? 'border-emerald-500 bg-emerald-500' : isLight ? 'border-stone-400' : 'border-stone-600'}`}>
                            {taxMethod === opt.val && <div className="w-1 h-1 rounded-full bg-white" />}
                          </div>
                          <span className={`text-xs ${textColor}`}>{opt.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-gradient-to-r ${theme.buttonGradient} backdrop-blur-sm border ${theme.accentBorder} rounded-r-xl py-8 px-2 transition-all duration-500`} style={{ left: isSidebarOpen ? '288px' : '0' }}>
          <span className={`${isLight ? 'text-white' : theme.highlight} text-xs font-medium tracking-widest`} style={{ writingMode: 'vertical-rl' }}>{isSidebarOpen ? 'CLOSE' : 'PARAMETERS'}</span>
        </button>

        <div className="flex-1 p-8">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30" />
              <span className={`${theme.labelText} text-xs uppercase tracking-[0.3em]`}>Quantum Wealth Systems</span>
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30" />
            </div>
            <h1 className={`text-4xl font-extralight tracking-wide ${textColor} mb-4`}>Internal & External <span className={theme.highlight}>Returns</span></h1>
            <div className="relative inline-block">
              <button onClick={() => setIsThemeOpen(!isThemeOpen)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-2 ${theme.accentBg} border ${theme.accentBorder} ${isLight ? theme.highlight : theme.highlight}`}>
                <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${theme.accentGradient}`} />
                {theme.name}
                <svg className={`w-4 h-4 transition-transform ${isThemeOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {isThemeOpen && (
                <div className={`absolute top-full mt-2 left-1/2 -translate-x-1/2 ${isLight ? 'bg-white' : 'bg-stone-900'} border ${isLight ? 'border-stone-200' : 'border-stone-700'} rounded-xl shadow-2xl overflow-hidden z-50 min-w-56`}>
                  <div className="max-h-80 overflow-y-auto">
                    {Object.entries(themes).map(([key, t]) => (
                      <button key={key} onClick={() => { setCurrentTheme(key); setIsThemeOpen(false); }} className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-all ${currentTheme === key ? (isLight ? 'bg-stone-100' : 'bg-stone-800') : ''} ${isLight ? 'hover:bg-stone-50 text-stone-700' : 'hover:bg-stone-800 text-stone-300'}`}>
                        <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${t.accentGradient}`} />
                        <span className="flex-1">{t.name}</span>
                        {t.isLight && <span className="text-xs text-stone-400">Light</span>}
                        {currentTheme === key && <span className="text-emerald-500">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-center gap-2 mb-10">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-2 ${activeTab === tab.id ? `bg-gradient-to-r ${theme.accentGradient} ${theme.highlight} border ${theme.accentBorder} shadow-lg` : `${textMuted} ${isLight ? 'hover:bg-stone-100/50' : 'hover:bg-white/5'} border border-transparent`}`}>
                <span>{tab.icon}</span>{tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'current-asset' && (
            <div className="max-w-4xl mx-auto">
              <div className={`bg-gradient-to-br ${theme.card1} rounded-3xl p-10 border backdrop-blur-sm mb-8`}>
                <div className="text-center">
                  <p className={`text-xs uppercase tracking-[0.3em] ${theme.labelText} mb-4`}>Projected Account Value</p>
                  <p className={`text-7xl font-semibold ${textColor} tracking-tight mb-2`}>{formatMoney(calculateNetTaxOnlyValue())}</p>
                  <p className={`text-sm ${textMuted}`}>After {currentAsset.timePeriod} years • {taxMethod === 'net' ? 'Net' : 'Gross'} Tax Method</p>
                </div>
                <div className={`grid grid-cols-3 gap-6 mt-10 pt-8 border-t ${isLight ? 'border-stone-200/50' : 'border-stone-800/30'}`}>
                  <div className="text-center"><p className={`text-3xl font-semibold ${textColor}`}>{formatMoney(summary.totalPrincipal)}</p><p className={`text-xs ${textSubtle} uppercase tracking-wider mt-1`}>Total Principal</p></div>
                  <div className="text-center"><p className={`text-3xl font-semibold ${theme.highlight}`}>{formatMoney(calculateProjectedValue())}</p><p className={`text-xs ${textSubtle} uppercase tracking-wider mt-1`}>After Fees</p></div>
                  <div className="text-center"><p className={`text-3xl font-semibold ${(calculateProjectedValue() - (taxMethod === 'gross' ? summary.taxAmount + summary.taxOpportunityCost : 0) - summary.totalTermCosts - summary.termCostOpportunity - summary.totalPlanningFees - summary.planningFeeOpportunity) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{formatMoney(calculateProjectedValue() - (taxMethod === 'gross' ? summary.taxAmount + summary.taxOpportunityCost : 0) - summary.totalTermCosts - summary.termCostOpportunity - summary.totalPlanningFees - summary.planningFeeOpportunity)}</p><p className={`text-xs ${textSubtle} uppercase tracking-wider mt-1`}>Net After All Costs</p></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className={`${cardBg} rounded-2xl p-6 border ${cardBorder}`}>
                  <h3 className={`text-xs uppercase tracking-widest ${theme.labelText} mb-4`}>Built-in Deductions</h3>
                  <div className="space-y-3">
                    {taxMethod === 'net' && summary.taxAmount > 0 && <div className="flex justify-between items-center"><span className={textMuted}>Taxes ({currentAsset.taxes}%)</span><span className="text-rose-500 font-semibold text-lg">-{formatMoney(summary.taxAmount)}</span></div>}
                    {summary.managementFeesAmount > 0 && <div className="flex justify-between items-center"><span className={textMuted}>Management ({currentAsset.managementFees}%)</span><span className="text-rose-500 font-semibold text-lg">-{formatMoney(summary.managementFeesAmount)}</span></div>}
                  </div>
                </div>
                <div className={`${cardBg} rounded-2xl p-6 border ${cardBorder}`}>
                  <h3 className={`text-xs uppercase tracking-widest ${theme.labelText} mb-4`}>External Costs</h3>
                  <div className="space-y-3">
                    {taxMethod === 'gross' && summary.taxAmount > 0 && <div className="flex justify-between items-center"><span className={textMuted}>Taxes + Opp. Cost</span><span className="text-rose-500 font-semibold text-lg">{formatMoney(summary.taxAmount + summary.taxOpportunityCost)}</span></div>}
                    {summary.totalTermCosts > 0 && <div className="flex justify-between items-center"><span className={textMuted}>Term + Opp. Cost</span><span className="text-rose-500 font-semibold text-lg">{formatMoney(summary.totalTermCosts + summary.termCostOpportunity)}</span></div>}
                    {summary.totalPlanningFees > 0 && <div className="flex justify-between items-center"><span className={textMuted}>Planning + Opp. Cost</span><span className="text-rose-500 font-semibold text-lg">{formatMoney(summary.totalPlanningFees + summary.planningFeeOpportunity)}</span></div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'pli-values' && (
            <div className="max-w-4xl mx-auto">
              <div className={`bg-gradient-to-br ${theme.card2} rounded-3xl p-10 border backdrop-blur-sm mb-8 relative`}>
                <div className="flex items-center justify-between mb-8">
                  <h2 className={`text-xs uppercase tracking-[0.3em] ${theme.labelText} text-center flex-1`}>Policy Projections</h2>
                  <button 
                    onClick={() => setIsDetailsOpen(true)}
                    className={`px-4 py-2 rounded-xl text-xs font-medium transition-all duration-300 flex items-center gap-2 ${theme.accentBg} border ${theme.accentBorder} ${theme.highlight} hover:opacity-80`}
                  >
                    <span>◈</span> Details
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-6 mb-10">
                  <LuxuryInput label="Annual Premium" value={manualPliInputs.annualPremium} onChange={(v) => handleManualPliChange('annualPremium', v)} prefix="$" labelClass={labelClass} inputClass={inputClass} affixClass={affixClass} />
                  <LuxuryInput label="Future Cash Value" value={manualPliInputs.futureCashValue} onChange={(v) => handleManualPliChange('futureCashValue', v)} prefix="$" labelClass={labelClass} inputClass={inputClass} affixClass={affixClass} disabled={yearlyData.length > 0} />
                  <LuxuryInput label="Death Benefit" value={manualPliInputs.futureDeathBenefit} onChange={(v) => handleManualPliChange('futureDeathBenefit', v)} prefix="$" labelClass={labelClass} inputClass={inputClass} affixClass={affixClass} disabled={yearlyData.length > 0} />
                </div>
                
                {yearlyData.length > 0 && (
                  <div className={`mb-8 rounded-xl overflow-hidden border ${isLight ? 'border-stone-200/50' : 'border-stone-700/30'}`}>
                    <div className={`max-h-64 overflow-y-auto ${isLight ? 'bg-white/50' : 'bg-black/20'}`}>
                      <table className="w-full text-sm">
                        <thead className={`sticky top-0 ${isLight ? 'bg-stone-100' : 'bg-stone-800'}`}>
                          <tr>
                            <th className={`px-4 py-3 text-left text-xs uppercase tracking-wider ${theme.labelText}`}>Year</th>
                            <th className={`px-4 py-3 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Premium</th>
                            <th className={`px-4 py-3 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Cash Value</th>
                            <th className={`px-4 py-3 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Death Benefit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {yearlyData.map((row, idx) => (
                            <tr key={idx} className={`border-t ${isLight ? 'border-stone-200/30' : 'border-stone-700/30'} ${row.year === selectedYear ? (isLight ? 'bg-emerald-100/50' : 'bg-emerald-900/20') : ''}`}>
                              <td className={`px-4 py-2 ${textColor} ${row.year === selectedYear ? 'font-semibold' : ''}`}>{row.year}</td>
                              <td className={`px-4 py-2 text-right ${textColor} ${row.year === selectedYear ? 'font-semibold' : ''}`}>${formatMoney(row.premium)}</td>
                              <td className={`px-4 py-2 text-right ${textColor} ${row.year === selectedYear ? 'font-semibold' : ''}`}>${formatMoney(row.cashValue)}</td>
                              <td className={`px-4 py-2 text-right ${textColor} ${row.year === selectedYear ? 'font-semibold' : ''}`}>${formatMoney(row.deathBenefit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className={`px-4 py-3 ${isLight ? 'bg-stone-50' : 'bg-stone-800/50'} border-t ${isLight ? 'border-stone-200/50' : 'border-stone-700/30'} flex justify-between items-center`}>
                      <p className={`text-xs ${textMuted}`}>{yearlyData.length} years of data • Scale: {basePremium > 0 ? ((parseFloat(manualPliInputs.annualPremium) / basePremium) * 100).toFixed(0) : 100}%</p>
                      <button onClick={clearYearlyData} className={`text-xs ${textMuted} hover:text-rose-500 transition-colors`}>Clear Data</button>
                    </div>
                  </div>
                )}

                <div className={`grid grid-cols-2 gap-8 pt-8 border-t ${isLight ? 'border-stone-200/50' : 'border-stone-800/30'}`}>
                  <div className={`text-center p-6 ${isLight ? 'bg-white/40' : 'bg-black/20'} rounded-2xl`}>
                    <p className={`text-xs uppercase tracking-widest ${textSubtle} mb-3`}>Cash Value IRR</p>
                    <p className={`text-5xl font-semibold ${pliAnalysis.cashValueIRR >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{pliAnalysis.cashValueIRR.toFixed(2)}%</p>
                    <p className={`text-sm ${textSubtle} mt-2`}>Year {currentAsset.timePeriod}</p>
                  </div>
                  <div className={`text-center p-6 ${isLight ? 'bg-white/40' : 'bg-black/20'} rounded-2xl`}>
                    <p className={`text-xs uppercase tracking-widest ${textSubtle} mb-3`}>Death Benefit IRR</p>
                    <p className={`text-5xl font-semibold ${pliAnalysis.deathBenefitIRR >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{pliAnalysis.deathBenefitIRR.toFixed(2)}%</p>
                    <p className={`text-sm ${textSubtle} mt-2`}>Year {currentAsset.timePeriod}</p>
                  </div>
                </div>
              </div>
              <div className={`${cardBg} rounded-2xl p-6 border ${cardBorder}`}>
                <div className="grid grid-cols-4 gap-6 text-center">
                  <div><p className={`text-3xl font-semibold ${textColor}`}>{formatMoney(pliAnalysis.totalPremiums)}</p><p className={`text-xs ${textSubtle} uppercase tracking-wider mt-1`}>Total Premiums</p></div>
                  <div><p className="text-3xl font-semibold text-emerald-500">{formatMoney(pliAnalysis.cashValue)}</p><p className={`text-xs ${textSubtle} uppercase tracking-wider mt-1`}>Cash Value</p></div>
                  <div><p className={`text-3xl font-semibold ${theme.highlight}`}>{formatMoney(pliAnalysis.deathBenefit)}</p><p className={`text-xs ${textSubtle} uppercase tracking-wider mt-1`}>Death Benefit</p></div>
                  <div><p className="text-3xl font-semibold text-cyan-500">{formatMoney(pliAnalysis.deathBenefit - pliAnalysis.cashValue)}</p><p className={`text-xs ${textSubtle} uppercase tracking-wider mt-1`}>Net DB</p></div>
                </div>
              </div>

              {/* Details Popup Modal */}
              {isDetailsOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsDetailsOpen(false)} />
                  <div className={`relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl ${isLight ? 'bg-white' : 'bg-stone-900'} border ${isLight ? 'border-stone-200' : 'border-stone-700'} shadow-2xl`}>
                    <div className={`sticky top-0 ${isLight ? 'bg-white' : 'bg-stone-900'} p-6 border-b ${isLight ? 'border-stone-200' : 'border-stone-700'} flex items-center justify-between`}>
                      <h3 className={`text-lg font-light ${textColor}`}>Policy Data Entry</h3>
                      <button onClick={() => setIsDetailsOpen(false)} className={`w-8 h-8 rounded-full ${isLight ? 'bg-stone-100 hover:bg-stone-200' : 'bg-stone-800 hover:bg-stone-700'} flex items-center justify-center transition-colors`}>
                        <span className={textMuted}>✕</span>
                      </button>
                    </div>
                    
                    <div className="p-6 space-y-6">
                      {/* Base Premium Input */}
                      <div>
                        <label className={labelClass}>Base Annual Premium</label>
                        <div className="relative">
                          <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${affixClass}`}>$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={formatWithCommas(popupPremium)}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/,/g, '');
                              if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                                setPopupPremium(raw);
                              }
                            }}
                            className={`w-full ${isLight ? 'bg-stone-50' : 'bg-black/30'} border ${theme.inputBorder} rounded-xl py-3 pl-8 pr-4 ${textColor} text-right font-light tracking-wide focus:outline-none ${theme.inputFocus} transition-all duration-300`}
                          />
                        </div>
                        <p className={`text-xs ${textMuted} mt-2`}>This is the base premium used for proportional scaling</p>
                      </div>

                      {/* Paste Boxes */}
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className={labelClass}>Cash Values (one per line)</label>
                          <textarea
                            value={cashValuePaste}
                            onChange={(e) => setCashValuePaste(e.target.value)}
                            placeholder="Paste cash values here...&#10;15000&#10;32000&#10;51000&#10;..."
                            className={`w-full h-40 p-4 rounded-xl border ${theme.inputBorder} ${isLight ? 'bg-stone-50' : 'bg-black/30'} ${textColor} text-sm focus:outline-none ${theme.inputFocus} font-mono`}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Death Benefits (one per line)</label>
                          <textarea
                            value={deathBenefitPaste}
                            onChange={(e) => setDeathBenefitPaste(e.target.value)}
                            placeholder="Paste death benefits here...&#10;500000&#10;500000&#10;500000&#10;..."
                            className={`w-full h-40 p-4 rounded-xl border ${theme.inputBorder} ${isLight ? 'bg-stone-50' : 'bg-black/30'} ${textColor} text-sm focus:outline-none ${theme.inputFocus} font-mono`}
                          />
                        </div>
                      </div>

                      {/* Apply Button */}
                      <button 
                        onClick={applyPastedData} 
                        disabled={(!cashValuePaste.trim() && !deathBenefitPaste.trim()) || !popupPremium || parseFloat(popupPremium) <= 0}
                        className={`w-full py-4 rounded-xl bg-gradient-to-r ${theme.buttonGradient} text-white font-medium transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-lg`}
                      >
                        Apply Data
                      </button>

                      {/* Preview Table */}
                      {(cashValuePaste.trim() || deathBenefitPaste.trim()) && (
                        <div>
                          <label className={labelClass}>Preview</label>
                          <div className={`rounded-xl overflow-hidden border ${isLight ? 'border-stone-200' : 'border-stone-700'}`}>
                            <div className={`max-h-48 overflow-y-auto ${isLight ? 'bg-stone-50' : 'bg-black/20'}`}>
                              <table className="w-full text-sm">
                                <thead className={`sticky top-0 ${isLight ? 'bg-stone-100' : 'bg-stone-800'}`}>
                                  <tr>
                                    <th className={`px-4 py-2 text-left text-xs uppercase tracking-wider ${theme.labelText}`}>Year</th>
                                    <th className={`px-4 py-2 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Premium</th>
                                    <th className={`px-4 py-2 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Cash Value</th>
                                    <th className={`px-4 py-2 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Death Benefit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const cvPreview = parseColumnData(cashValuePaste);
                                    const dbPreview = parseColumnData(deathBenefitPaste);
                                    const maxLen = Math.max(cvPreview.length, dbPreview.length);
                                    const premiumVal = parseFloat(popupPremium) || 0;
                                    return Array.from({length: Math.min(maxLen, 10)}, (_, i) => (
                                      <tr key={i} className={`border-t ${isLight ? 'border-stone-200/50' : 'border-stone-700/30'}`}>
                                        <td className={`px-4 py-2 ${textColor}`}>{i + 1}</td>
                                        <td className={`px-4 py-2 text-right ${textColor}`}>${formatMoney(premiumVal)}</td>
                                        <td className={`px-4 py-2 text-right ${textColor}`}>${formatMoney(cvPreview[i] || 0)}</td>
                                        <td className={`px-4 py-2 text-right ${textColor}`}>${formatMoney(dbPreview[i] || 0)}</td>
                                      </tr>
                                    ));
                                  })()}
                                </tbody>
                              </table>
                            </div>
                            {Math.max(parseColumnData(cashValuePaste).length, parseColumnData(deathBenefitPaste).length) > 10 && (
                              <div className={`px-4 py-2 ${isLight ? 'bg-stone-100' : 'bg-stone-800'} text-xs ${textMuted} text-center`}>
                                Showing first 10 of {Math.max(parseColumnData(cashValuePaste).length, parseColumnData(deathBenefitPaste).length)} years
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Existing Data */}
                      {yearlyData.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className={labelClass}>Current Data</label>
                            <button onClick={clearYearlyData} className={`text-xs ${textMuted} hover:text-rose-500 transition-colors`}>Clear All</button>
                          </div>
                          <div className={`rounded-xl overflow-hidden border ${isLight ? 'border-stone-200' : 'border-stone-700'}`}>
                            <div className={`max-h-48 overflow-y-auto ${isLight ? 'bg-stone-50' : 'bg-black/20'}`}>
                              <table className="w-full text-sm">
                                <thead className={`sticky top-0 ${isLight ? 'bg-stone-100' : 'bg-stone-800'}`}>
                                  <tr>
                                    <th className={`px-4 py-2 text-left text-xs uppercase tracking-wider ${theme.labelText}`}>Year</th>
                                    <th className={`px-4 py-2 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Premium</th>
                                    <th className={`px-4 py-2 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Cash Value</th>
                                    <th className={`px-4 py-2 text-right text-xs uppercase tracking-wider ${theme.labelText}`}>Death Benefit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {yearlyData.map((row, idx) => (
                                    <tr key={idx} className={`border-t ${isLight ? 'border-stone-200/50' : 'border-stone-700/30'}`}>
                                      <td className={`px-4 py-2 ${textColor}`}>{row.year}</td>
                                      <td className={`px-4 py-2 text-right ${textColor}`}>${formatMoney(row.premium)}</td>
                                      <td className={`px-4 py-2 text-right ${textColor}`}>${formatMoney(row.cashValue)}</td>
                                      <td className={`px-4 py-2 text-right ${textColor}`}>${formatMoney(row.deathBenefit)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className={`px-4 py-2 ${isLight ? 'bg-stone-100' : 'bg-stone-800'} text-xs ${textMuted}`}>
                              Base Premium: ${formatMoney(basePremium)} • Current Scale: {basePremium > 0 ? ((parseFloat(manualPliInputs.annualPremium) / basePremium) * 100).toFixed(0) : 100}%
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'comparison' && (
            <div className="max-w-5xl mx-auto">
              <div className={`${cardBg} rounded-xl px-6 py-2 border ${cardBorder} mb-8`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs uppercase tracking-widest ${theme.labelText}`}>Timeline</span>
                  <div className={`px-3 py-1 rounded-lg ${theme.accentBg} border ${theme.accentBorder}`}>
                    <span className={`text-sm font-light ${theme.highlight}`}>Year {selectedYear}</span>
                    <span className={`text-xs ${textMuted} ml-1`}>of {currentAsset.timePeriod || 1}</span>
                  </div>
                </div>
                <div className="relative py-1">
                  <div className={`absolute top-1/2 left-0 right-0 h-0.5 rounded-full ${isLight ? 'bg-stone-200' : 'bg-stone-700'} -translate-y-1/2`}></div>
                  <div 
                    className="absolute top-1/2 left-0 h-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 -translate-y-1/2 transition-all duration-150"
                    style={{ width: `${Math.max(1, parseInt(currentAsset.timePeriod) || 1) > 1 ? ((selectedYear - 1) / (Math.max(1, parseInt(currentAsset.timePeriod) || 1) - 1)) * 100 : 100}%` }}
                  ></div>
                  <input
                    type="range"
                    min="1"
                    max={Math.max(1, parseInt(currentAsset.timePeriod) || 1)}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="relative w-full h-4 appearance-none cursor-pointer bg-transparent z-10"
                    style={{
                      WebkitAppearance: 'none',
                    }}
                  />
                  <style>{`
                    input[type="range"]::-webkit-slider-thumb {
                      -webkit-appearance: none;
                      width: 14px;
                      height: 14px;
                      border-radius: 50%;
                      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                      box-shadow: 0 1px 4px rgba(16, 185, 129, 0.4), 0 0 0 2px ${isLight ? 'rgba(255,255,255,0.9)' : 'rgba(30,30,30,0.9)'};
                      cursor: pointer;
                      transition: transform 0.15s ease, box-shadow 0.15s ease;
                    }
                    input[type="range"]::-webkit-slider-thumb:hover {
                      transform: scale(1.15);
                      box-shadow: 0 2px 6px rgba(16, 185, 129, 0.5), 0 0 0 3px ${isLight ? 'rgba(255,255,255,0.9)' : 'rgba(30,30,30,0.9)'};
                    }
                    input[type="range"]::-moz-range-thumb {
                      width: 14px;
                      height: 14px;
                      border: none;
                      border-radius: 50%;
                      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                      box-shadow: 0 1px 4px rgba(16, 185, 129, 0.4), 0 0 0 2px ${isLight ? 'rgba(255,255,255,0.9)' : 'rgba(30,30,30,0.9)'};
                      cursor: pointer;
                    }
                    input[type="range"]::-webkit-slider-runnable-track {
                      height: 2px;
                      background: transparent;
                    }
                    input[type="range"]::-moz-range-track {
                      height: 2px;
                      background: transparent;
                    }
                  `}</style>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-8">
                <div className={`bg-gradient-to-br ${theme.card3} rounded-3xl p-8 border`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-xl ${isLight ? 'bg-blue-100' : 'bg-blue-500/20'} flex items-center justify-center`}><span className="text-blue-400">◈</span></div>
                    <h3 className={`text-lg font-light ${textColor}`}>Traditional Investment</h3>
                  </div>
                  <div className="space-y-4 mb-8">
                    <div className={`flex justify-between py-2 border-b ${isLight ? 'border-stone-200/50' : 'border-stone-800/50'}`}><span className={textMuted}>Account Value</span><span className={`${textColor} font-semibold text-xl`}>{formatMoney(comparison.external.accountValue)}</span></div>
                    <Toggle label="Include Management Fees" checked={includeManagementFees} onChange={() => setIncludeManagementFees(!includeManagementFees)} />
                    <Toggle label="Include External Costs" checked={includeExternalCosts} onChange={() => setIncludeExternalCosts(!includeExternalCosts)} />
                  </div>
                  <div className={`${isLight ? 'bg-white/40' : 'bg-black/30'} rounded-2xl p-6 text-center`}>
                    <p className={`text-xs uppercase tracking-widest ${textSubtle} mb-2`}>Net Wealth</p>
                    <p className={`text-5xl font-semibold ${textColor}`}>{formatMoney(comparison.external.netValue)}</p>
                    <p className="text-lg text-blue-500 mt-2 font-medium">{comparison.external.externalRateOfReturn.toFixed(2)}% {(includeManagementFees || includeExternalCosts) ? 'ExRR' : 'IRR'}</p>
                  </div>
                </div>
                <div className={`bg-gradient-to-br ${theme.card2} rounded-3xl p-8 border`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-xl ${isLight ? 'bg-emerald-100' : 'bg-emerald-500/20'} flex items-center justify-center`}><span className="text-emerald-400">◆</span></div>
                    <h3 className={`text-lg font-light ${textColor}`}>PLI Strategy</h3>
                  </div>
                  {comparison.internal ? (
                    <>
                      <div className="space-y-4 mb-8">
                        <div className={`flex justify-between py-2 border-b ${isLight ? 'border-stone-200/50' : 'border-stone-800/50'}`}><span className={textMuted}>Cash Value</span><span className={`${textColor} font-semibold text-xl`}>{formatMoney(comparison.internal.finalCashValue)}</span></div>
                        <div className={`flex justify-between py-2 border-b ${isLight ? 'border-stone-200/50' : 'border-stone-800/50'}`}><span className={textMuted}>Net Death Benefit</span><span className={`${textColor} font-semibold text-xl`}>{formatMoney(comparison.internal.netDeathBenefit)}</span></div>
                        <Toggle label="Include Recovered Costs" checked={includeRecoveredCosts} onChange={() => setIncludeRecoveredCosts(!includeRecoveredCosts)} />
                      </div>
                      <div className={`${isLight ? 'bg-white/40' : 'bg-black/30'} rounded-2xl p-6 text-center`}>
                        <p className={`text-xs uppercase tracking-widest ${textSubtle} mb-2`}>Total Wealth</p>
                        <p className="text-5xl font-semibold text-emerald-500">{formatMoney(comparison.internal.totalAssets)}</p>
                        <p className="text-lg text-emerald-500 mt-2 font-medium">{comparison.internal.externalRateOfReturn.toFixed(2)}% {includeRecoveredCosts ? 'ExRR' : 'IRR'}</p>
                      </div>
                    </>
                  ) : <div className="text-center py-12"><p className={textMuted}>Enter PLI values to compare</p></div>}
                </div>
              </div>
              {comparison.internal && (
                <div className={`mt-8 bg-gradient-to-r ${theme.banner} rounded-2xl p-8 border text-center`}>
                  <p className={`text-xs uppercase tracking-widest ${theme.labelText} mb-3`}>Wealth Advantage</p>
                  <p className={`text-6xl font-bold ${theme.bannerText}`}>+{formatMoney(comparison.internal.totalAssets - comparison.external.netValue)}</p>
                  <p className={`${textMuted} mt-3`}>{comparison.external.netValue > 0 ? `${(((comparison.internal.totalAssets - comparison.external.netValue) / comparison.external.netValue) * 100).toFixed(1)}% additional wealth` : ''} at Year {selectedYear}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LuxuryCalculator;